import { useCallback } from 'react';
import { useChainId } from 'wagmi';
import { PoolKey } from '@uniswap/v4-sdk';
import { Token } from '@uniswap/sdk-core';
import { Pool, Position } from '@uniswap/v4-sdk';
import { verifiedPoolsPositionManagerAbi } from '@generated/generated';
import { useWriteContractWithReceipt } from '@/onchain/hooks/useWriteContractWithReceipt';
import { PoolState } from '@/onchain/pools';
import { toBigint } from '@/utils/bigInt';
import { CurrencyField } from '@/types';
import { getRemoveLiquidityArgs } from '@/onchain/positionManager';
import { assetByAddress } from '@/onchain/assets';
import { toJSBI } from '@/utils/bigInt';
import type { RemoveLiquidityParams } from '@/types';

export const useCalculateLiquidity = ({
  chainId,
  poolKey,
  poolState,
  tickLower,
  tickUpper,
  independentField,
  independentAmount,
}: {
  chainId: number | undefined;
  poolState: PoolState | undefined;
  poolKey: PoolKey | undefined;
  tickLower: number;
  tickUpper: number;
  independentAmount: bigint;
  independentField: CurrencyField | undefined;
}) => {
  if (
    !poolState ||
    !independentField ||
    independentAmount == undefined ||
    !chainId ||
    !poolKey ||
    // edge case: when a user adjusts tickLower first, then tickUpper,
    // tickLower can be greater than tickUpper temporarily
    // e.g. changing from [0.9, 1.1] to [4, 5]
    tickLower >= tickUpper
  ) {
    return {
      liquidity: 0n,
      dependentFieldAmount: 0n,
    };
  }

  try {
    const singleSidedDeposit = Boolean(
      tickUpper && poolState && (poolState.tick <= tickLower || poolState.tick >= tickUpper),
    );

    const currency0 = new Token(
      chainId,
      poolKey.currency0,
      assetByAddress(chainId, poolKey.currency0)?.exponent ?? 0,
    );
    const currency1 = new Token(
      chainId,
      poolKey.currency1,
      assetByAddress(chainId, poolKey.currency1)?.exponent ?? 0,
    );

    const pool = new Pool(
      currency0,
      currency1,
      poolKey.fee,
      poolKey.tickSpacing,
      poolKey.hooks,
      toJSBI(poolState.sqrtPriceX96),
      0,
      poolState.tick,
      [],
    );

    const position =
      independentField === CurrencyField.CURRENCY_0
        ? Position.fromAmount0({
            pool,
            tickLower,
            tickUpper,
            amount0: toJSBI(independentAmount),
            useFullPrecision: true,
          })
        : Position.fromAmount1({
            pool,
            tickLower,
            tickUpper,
            amount1: toJSBI(independentAmount),
          });

    if (singleSidedDeposit) {
      return {
        liquidity: toBigint(position.liquidity),
        dependentFieldAmount: 0n,
      };
    }

    // TODO: really weird issue here, the actual amount that will be transferred is dependentFieldAmount + 1n
    // TODO: a very hacky fix for now
    return {
      liquidity: toBigint(position.liquidity),
      dependentFieldAmount:
        (independentField === CurrencyField.CURRENCY_0
          ? toBigint(position.amount1.quotient)
          : toBigint(position.amount0.quotient)) + 1n,
    };
  } catch (e) {
    console.error('Error calculating liquidity', e);
    return {
      liquidity: 0n,
      dependentFieldAmount: 0n,
    };
  }
};

export const useRemoveLiquidity = (removeLiquidityParams: RemoveLiquidityParams) => {
  const chainId = useChainId();

  const {
    hash,
    // write state
    writeError,
    isWriteError,
    isWritePending,
    // confirm state
    confirmError,
    isConfirmLoading,
    isConfirmSuccess,
    isConfirmError,
    // tx receipt
    receipt,
    // write function
    writeContract,
    writeContractAsync,
    reset,
  } = useWriteContractWithReceipt();

  const { data, positionManagerAddress } = getRemoveLiquidityArgs(chainId, removeLiquidityParams);
  const write = useCallback(() => {
    writeContract({
      address: positionManagerAddress,
      abi: verifiedPoolsPositionManagerAbi,
      functionName: 'multicall',
      args: [data],
    });
  }, [writeContract, positionManagerAddress, data]);

  const writeAsync = useCallback(async () => {
    await writeContractAsync({
      address: positionManagerAddress,
      abi: verifiedPoolsPositionManagerAbi,
      functionName: 'multicall',
      args: [data],
    });
  }, [writeContractAsync, positionManagerAddress, data]);

  return {
    hash,
    writeError,
    isWriteError,
    isWritePending,
    confirmError,
    isConfirmLoading,
    isConfirmSuccess,
    isConfirmError,
    receipt,
    write,
    writeAsync,
    reset,
  };
};
